import React, {useEffect, useRef, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {Helmet} from "react-helmet";
import { io } from "socket.io-client";
import { publicRoutes, privateRoutes } from "./routes/routes";
import ProtectedRoute from "./routes/ProtectedRoute";
import { paths } from "./constants/paths";

import Page404 from "./Screens/404page";
import Loader from "./UI/Loader";
import NavBar from "./Components/NavBar";
import PaymentModal from "./Components/Modals/PaymentModal";
import MobileModal from "./Components/Modals/MobileModal";
import useGetAccountApi from "./Global/Api/Account/getAccountApi";
import {
  setLastMessage,
  setNewMessageInChat,
  setOnline, setUserData,
  updateTotalUnreadMessage,
} from "./Store/Slice/messagingSlice";
import useMarkAsRead from "./Global/Api/Messaging/markAsReadApi";
import "react-toastify/dist/ReactToastify.css";
import "./Global/app.scss";

export const App = () => {
  const dispatch = useDispatch();
  const { get } = useGetAccountApi();
  const socket = useRef(null);
  const location = useLocation();
  const locationMessaging = useRef(false)
  const markAsReadApi = useMarkAsRead();
  const connected = useRef(false);
  const loader = useSelector((state) => state.rootReducer.techsbookSlice.loader);
  const { paymentModal } = useSelector((state) => state.rootReducer.techsbookSlice);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const token = localStorage.getItem("techsbook_customer_portal_token");

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("downloadApp") === "true") {
            const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
            const isAndroid = /Android/i.test(navigator.userAgent);
            if (isIOS) {
                window.location.replace("https://apps.apple.com/kg/app/techsbook-direct-booking/id6761090182");
            } else if (isAndroid) {
                window.location.replace("https://play.google.com/store/apps/details?id=com.techsbook.customer");
            }
            return;
        }

        const isMobile =
            /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
                navigator.userAgent
            ) || window.innerWidth <= 768;

        if (!token && isMobile) {
            setShowMobileModal(true);
        }
    }, []);

    const handleCloseMobileModal = () => {
        setShowMobileModal(false);
    };

    useEffect(() => {
        if (showMobileModal) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }

        return () => document.body.classList.remove("no-scroll");
    }, [showMobileModal]);


    useEffect(() => {
    const publicKey =
      "BJexGlGdZ_YjEYHi0xVq3eRMYYnz84N5pod8PoqGAppuKj8nDaHGHfIFNMAZDrlsCzToUQ03Rp_kU-sdSZS2TO8";
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw-v3.js?hello=hi")
        .then((registration) => {
          return registration.pushManager
            .getSubscription()
            .then((subscription) => {
              if (!subscription) {
                return registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: publicKey,
                });
              }
            });
        })
        .then((subscription) => {
          if (subscription) {
            fetch(
              `${process.env.REACT_APP_API_URL_DEVELOP}/account/update-device-token`,
              {
                method: "POST",
                body: JSON.stringify(subscription),
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }
        });
    }
  }, []);

  useEffect(() => {
    if(location?.pathname !== paths.messaging) {
      dispatch(setUserData({ is_online: false }));
      localStorage.removeItem("userDataId");
      locationMessaging.value = false
    }
    if(location?.pathname === paths.messaging) {
      locationMessaging.value = true
    }
  }, [location])

  const publicPaths = Object.values(paths.auth);

  useEffect(() => {
    if (!publicPaths.includes(location?.pathname)) {
      get();
    }
  }, []);

  useEffect(() => {
    if(token) {
      if (!connected.value) {
        socket.current = io(`${process.env.REACT_APP_SOCKET_URL}`, {
          extraHeaders: {
            token: token,
            provider: "customer",
          },
        });
        socket.current.on("connect", () => {});

        socket.current.on("user_online", (data) => {
          dispatch(setOnline({ member_id: data?.user_id, isOnline: 1 }));
        });

        socket.current.on("user_offline", (data) => {
          dispatch(setOnline({ member_id: data?.user_id, isOnline: null }));
        });

        socket.current.on("new_message", (data) => {
          if (!locationMessaging.value) {
            dispatch(updateTotalUnreadMessage(1));
          }
          dispatch(setLastMessage({ id: data?.id, message: data }));
          dispatch(setNewMessageInChat({ id: data.id, content: data }));
          if (Number(localStorage.getItem("userDataId")) === Number(data.id)) {
            markAsReadApi.create(data);
          }
        });
        connected.value = true;
      }
    }
  }, [connected, token]);

  const navbarDisabled = () => {
    return !(
      location?.pathname === paths.registerInstallations ||
      location?.pathname === paths.registerProviders ||
      location?.pathname === paths.registerDetails ||
      location?.pathname === paths.auth.checkEmailProceed
    );
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={5000} />
      {loader && <Loader />}
      {paymentModal && <PaymentModal />}
      {showMobileModal && <MobileModal onClose={handleCloseMobileModal} />}
      <div
        className={
          token && navbarDisabled() ? `nav__wrapper` : ""
        }
      >
        {token && navbarDisabled() && <NavBar socket={socket} />}
        <div
          className={
            navbarDisabled()
              ? `nav__wrapper-right ${!token ? "nav__wrapper-right_none" : ""}`
              : ""
          }
        >
          {
            process.env.REACT_APP_ENV === 'production' &&
              <Helmet>
                <script async src="https://www.googletagmanager.com/gtag/js?id=G-XCSZ032YD3 (https://www.googletagmanager.com/gtag/js?id=G-XCSZ032YD3)"></script>
                <script>
                  {`
                    window.dataLayer = window.dataLayer || []; 
                    function gtag(){
                      dataLayer.push(arguments);
                    } 
                    gtag('js', new Date()); 
                    gtag('config', 'G-XCSZ032YD3'); 
                  `}
                </script>
              </Helmet>
          }
          <Routes>
            {publicRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
            <Route element={<ProtectedRoute />}>
              {privateRoutes.map(({ path, element, children }) => (
                <Route key={path} path={path} element={element}>
                  {children?.map((child) => (
                    <Route key={child.path} path={child.path} element={child.element} />
                  ))}
                </Route>
              ))}
            </Route>
            <Route path="/payment-result" element={<>Success</>} />
            <Route path="/*" element={<Page404 />} />
          </Routes>
        </div>
      </div>
    </>
  );
};

export default App;
